import React, { useEffect, useState } from 'react';
import {CATEGORY_DETAILS, PROJECT_NAME, TITLE} from '../ISRConstants';
import { TConnector, TField, TIndexState, TProductCategory, TSubmodel, TUpsertISRProjectDataModelsInput } from '../Pages/DefineModel/model';
import Card from 'Components/DesignSystem/Card/Card';
import TextField from 'Components/DesignSystem/TextField/TextField';
import { useCategoryUpdate } from './hooks/useCategoryUpdate';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import { RestrictionsOptions, RestrictionsToggle } from './RestrictionsControl';

type TCategoryComponentProps = {
    selectedItem: TField | TSubmodel | TProductCategory | TConnector | null;
    setUserProjectDataModels: React.Dispatch<React.SetStateAction<TUpsertISRProjectDataModelsInput>>;
    userProjectDataModels: TUpsertISRProjectDataModelsInput;
    indexInfo: TIndexState;
}

const CategoryComponent = ({selectedItem, setUserProjectDataModels, userProjectDataModels, indexInfo}: TCategoryComponentProps) => {
    const [fieldName, setFieldName] = useState<string>('');
    const { updateCategoryField } = useCategoryUpdate(selectedItem, userProjectDataModels, setUserProjectDataModels, indexInfo);

    useEffect(() => {
        const productCategory = selectedItem as TProductCategory;
        if (productCategory?.data?.belongsToDataModel ?? false) {
            setFieldName(userProjectDataModels?.project.dataModels[indexInfo?.dataModelIndex ?? -1]?.data?.productCategories[indexInfo?.categoryIndex ?? -1]?.data?.name ?? "");
        } else {
            setFieldName(userProjectDataModels?.project.productCategories[indexInfo?.categoryOutsideDataModelIndex ?? -1]?.data?.name ?? "");
        }
    }, [selectedItem, userProjectDataModels, indexInfo]);

    const handleCategoryNameChange = (value: string) => {
        setFieldName(value);
        updateCategoryField('name', value);
    };

    const productCategory = selectedItem as TProductCategory;

    // Derive restrictions state
    const restrictionsEnabled = !!(productCategory?.data?.recommended) || !!(productCategory?.data?.required);

    // Backward-compat: old required=true (no recommended field) → Recommended mode
    const isRecommended: boolean = productCategory?.data?.recommended !== undefined
        ? !!(productCategory?.data?.recommended)
        : !!(productCategory?.data?.required) && !(productCategory?.data?.blockAddToCart) && !(productCategory?.data?.lockNavigation);

    const isRequired: boolean = productCategory?.data?.required !== undefined && !isRecommended
        ? !!(productCategory?.data?.required)
        : false;

    const isBlockAddToCart = !!(productCategory?.data?.blockAddToCart);
    const isLockNavigation = !!(productCategory?.data?.lockNavigation);

    const applyRestrictionsPatch = (patch: Partial<{
        recommended: boolean; required: boolean; blockAddToCart: boolean; lockNavigation: boolean;
    }>) => {
        (Object.keys(patch) as Array<keyof typeof patch>).forEach((key) => {
            updateCategoryField(key as keyof TProductCategory['data'], patch[key] as any);
        });
    };

    const handleRestrictionsToggle = (checked: boolean) => {
        if (checked) {
            applyRestrictionsPatch({ recommended: true, required: false, blockAddToCart: false, lockNavigation: false });
        } else {
            applyRestrictionsPatch({ recommended: false, required: false, blockAddToCart: false, lockNavigation: false });
        }
    };

    const handleSelectRecommended = () => {
        applyRestrictionsPatch({ recommended: true, required: false, blockAddToCart: false, lockNavigation: false });
    };

    const handleSelectRequired = () => {
        applyRestrictionsPatch({ recommended: false, required: true, blockAddToCart: true, lockNavigation: false });
    };

    const handleBlockAddToCart = (checked: boolean) => {
        if (!checked && !isLockNavigation) {
            toast.warning('Required must have at least one enforcement enabled. Enable Lock Navigation first or disable Required.');
            return;
        }
        updateCategoryField('blockAddToCart', checked);
    };

    const handleLockNavigation = (checked: boolean) => {
        if (!checked && !isBlockAddToCart) {
            toast.warning('Required must have at least one enforcement enabled. Enable Block Add to Cart first or disable Required.');
            return;
        }
        updateCategoryField('lockNavigation', checked);
    };

    /*
        Hardcoded rather than taken from a prop: this component has never accepted an
        `id`, and its call site (`CategoryField`) does not pass one. Left as-is so the
        existing DOM ids keep resolving; if a second instance ever renders on one page
        these collide, and the fix is an `id` prop threaded from `CategoryField`.
    */
    const baseId = 'category-component';

    return (
        <Card id={baseId} data-testid={baseId} title={CATEGORY_DETAILS}>
            <div id={`${baseId}-header`} className="tw-flex tw-items-start tw-gap-3">
                <TextField
                    id={`${baseId}-name-input`}
                    data-testid={`${baseId}-name-input`}
                    label={TITLE}
                    value={fieldName}
                    onChange={handleCategoryNameChange}
                    placeholder={PROJECT_NAME}
                    className="tw-flex-1"
                />
                <RestrictionsToggle
                    baseId={baseId}
                    enabled={restrictionsEnabled}
                    onEnabledChange={handleRestrictionsToggle}
                />
            </div>

            <RestrictionsOptions
                baseId={baseId}
                enabled={restrictionsEnabled}
                mode={isRecommended ? 'recommended' : isRequired ? 'required' : null}
                onSelectRecommended={handleSelectRecommended}
                onSelectRequired={handleSelectRequired}
                blockAddToCart={isBlockAddToCart}
                onBlockAddToCartChange={handleBlockAddToCart}
                lockNavigation={isLockNavigation}
                onLockNavigationChange={handleLockNavigation}
                className="tw-mt-4"
            />
        </Card>
    );
}

export default CategoryComponent;
